/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ComponentProps, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ModalConfirm, Sidebar, Text, theme } from "@nordcloud/gnui";
import {
  useAddOrgUnitMutation,
  useUpdateBusinessContextMutation,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  GlobalSpinner,
  PopoverControls,
  ACTION,
} from "~/components";
import { useRemoveBusinessContext } from "~/hooks/graphql";
import { showSuccess, showError } from "~/services/toast";
import {
  generateActionConfirmText,
  generateActionErrorText,
  generateActionSuccessText,
  Maybe,
  toInputError,
} from "~/tools";
import { BusinessContextsForm } from "../BusinessContextsForm";
import { useSetPrimaryBusinessContext } from "../hooks";
import {
  OrganizationUnitForm,
  OrganizationUnitFormData,
} from "../OrganizationUnitForm";

type BusinessContextsFormData = NonNullable<
  ComponentProps<typeof BusinessContextsForm>["initialValues"]
>;

type Props = {
  businessContext: {
    id: string;
    name: string;
    color?: Maybe<string>;
    description?: Maybe<string>;
    isPrimary?: Maybe<boolean>;
  };
  onChange: () => void;
};

// TODO: Reduce amount of code
export function OrganizationUnitsHeader({ businessContext, onChange }: Props) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showOrgUnitForm, setShowOrgUnitForm] = useState(false);
  const [showBusinessContextForm, setShowBusinessContextForm] = useState(false);
  const [showPrimaryBusinessContextModal, setShowPrimaryBusinessContextModal] =
    useState(false);

  const navigate = useNavigate();

  const [addOrgUnitV2, { loading: isAddOrgUnitV2Loading }] =
    useAddOrgUnitMutation({
      onCompleted: onChange,
    });

  const [removeBusinessContext, { loading: isRemoveBusinessContextLoading }] =
    useRemoveBusinessContext();

  const [updateBusinessContext, { loading: isUpdateBusinessContextLoading }] =
    useUpdateBusinessContextMutation();

  const handleAddOrgUnit = async (values: OrganizationUnitFormData) => {
    try {
      setShowOrgUnitForm(false);
      await addOrgUnitV2({
        variables: {
          input: {
            ...values,
            contextId: businessContext.id,
          },
        },
      });
      showSuccess(generateActionSuccessText("Organization Unit")()("added")());
    } catch (error) {
      showError(generateActionErrorText(toInputError(error).message), {
        autoClose: false,
      });
    }
  };

  const handleDeleteBusinessContext = async () => {
    try {
      setShowDeleteModal(false);
      const response = await removeBusinessContext({
        id: businessContext.id,
      });

      if (response.data?.removeBusinessContext) {
        showSuccess(
          generateActionSuccessText("Business Context")()("deleted")()
        );
        navigate("/");
      }
    } catch (error) {
      showError(toInputError(error).message, {
        autoClose: false,
      });
    }
  };

  const handleUpdateBusinessContext = async (
    newValues: BusinessContextsFormData
  ) => {
    try {
      setShowBusinessContextForm(false);

      const { id: ignoredId, ...input } = newValues;
      await updateBusinessContext({
        variables: { id: businessContext.id, input },
      });

      showSuccess(generateActionSuccessText("Business Context")()("updated")());
    } catch (error) {
      showError(error.message, {
        autoClose: false,
      });
    }
  };

  const setPrimaryBusinessContext = useSetPrimaryBusinessContext({
    bcId: businessContext.id,
    onSuccess: () => {
      setShowPrimaryBusinessContextModal(false);
      onChange();
    },
  });

  const breadcrumbs = [
    {
      label: "Home",
      uri: "/",
    },
    {
      label: "Business Context",
      uri: "/",
      isDisabled: true,
    },
    {
      label: businessContext.name,
      uri: `/business-contexts/${businessContext.id}`,
    },
  ];

  const isLoading =
    isAddOrgUnitV2Loading ||
    isRemoveBusinessContextLoading ||
    isUpdateBusinessContextLoading;

  const items = [
    {
      label: "Edit Business Context",
      action: ACTION.EDIT,
      onClick: () => setShowBusinessContextForm(true),
    },
    {
      label: "Delete",
      action: ACTION.DELETE,
      onClick: () => setShowDeleteModal(true),
    },
    {
      label: "Set as primary Business Context",
      action: ACTION.CUSTOM_TRIGGER,
      onClick: () => setShowPrimaryBusinessContextModal(true),
      customIcon: "star" as const,
      disabled: isLoading,
    },
  ];

  const { name, id, isPrimary, color, description } = businessContext;

  return (
    <>
      <GlobalSpinner loading={isLoading} />
      <BreadcrumbsBox title={name} customBreadcrumbs={breadcrumbs}>
        <Button
          mr={theme.spacing.spacing04}
          icon="plus"
          onClick={() => setShowOrgUnitForm(true)}
        >
          Add new Organizational Unit
        </Button>
        <PopoverControls
          items={isPrimary ? items.slice(0, 2) : items}
          trigger={<PopoverTrigger />}
        />
      </BreadcrumbsBox>
      <Sidebar
        title="Edit Business Context"
        isOpen={showBusinessContextForm}
        onClick={() => {
          setShowBusinessContextForm(false);
        }}
      >
        <BusinessContextsForm
          isEditMode
          hasColor
          type="Business Contexts"
          initialValues={{
            name,
            color: color ?? "",
            description: description ?? "",
            id,
          }}
          onUpdate={handleUpdateBusinessContext}
        />
      </Sidebar>
      <OrganizationUnitForm
        editMode={false}
        isOpen={showOrgUnitForm}
        onClose={() => setShowOrgUnitForm(false)}
        onSubmit={handleAddOrgUnit}
      />
      <ModalConfirm
        data-testid="modal-confirm"
        isOpen={showDeleteModal}
        confirm={handleDeleteBusinessContext}
        actionLabel="Delete"
        contentLabel="Delete Business Context"
        onClose={() => setShowDeleteModal(false)}
      >
        {generateActionConfirmText(name)("delete")()}
      </ModalConfirm>
      <ModalConfirm
        isOpen={showPrimaryBusinessContextModal}
        confirm={setPrimaryBusinessContext}
        actionLabel="Set as primary Business Context"
        contentLabel="Set as primary Business Context"
        onClose={() => setShowPrimaryBusinessContextModal(false)}
      >
        <Text>
          Are you sure that you want to set {name} as a primary business
          context?
        </Text>
      </ModalConfirm>
    </>
  );
}

function PopoverTrigger() {
  return (
    <div
      data-testid="popover-trigger"
      css={{
        svg: {
          width: "2rem",
          height: "2rem",
        },
      }}
    >
      <Button severity="low" icon="menu" size="md" data-testid="popover" />
    </div>
  );
}
